/**
 * ValueAssessmentContext - Central State Management for Value Assessment Flow
 * 
 * This context manages the complete Value Assessment workflow including
 * - Project Kickoff (revenue band, entity count, regional FTE distribution)
 * - Strategic Context (pain points, implementation blockers, current tools)
 * - Platform Configuration (pillar selection, prioritization, pain point mapping)
 * - Value Modeling (task allocation, automation percentages, FTE distribution)
 * - Final Summary (ROI calculations, scenario analysis, export data preparation)
 * 
 * Key features:
 * - Real-time auto-save to valueSummary v2.1 blob structure
 * - Cross-screen state persistence and hydration
 * - Database-driven ROI calculations using calculation_rules
 * - Integration with pricing matrix for accurate cost modeling
 * - Export data preparation for PowerPoint, Excel, and PDF generation
 * 
 * Data flow:
 * User Input → Context State → Auto-save → Database (summary_data.valueSummary)
 * Page Load → Database → Context Hydration → Screen Population
 */

import {createContext, ReactNode, useContext, useEffect, useRef, useState, Dispatch, SetStateAction,} from "react";
import {useLocation, useParams} from "wouter";
import {ValueAssessmentData, ValueSummary} from "@shared/interfaces/valueAssessment.interface.ts";
import {ProjectPayload} from "@shared/interfaces/projectPayload.interface.ts";
import {useDebouncedAutoSave} from '@/hooks/useDebouncedAutoSave';
import { apiRequest } from "@/lib/queryClient";
import { setRuntimeActivityHelpTextConfig } from "@shared/constants/activityHelpTextV2";

// Create context
type UpdateSectionOptions = {
  autoSave?: boolean;
};

export type SaveSummarySectionOptions = {
  keepAlive?: boolean;
};

type ContextType = {
  data: ValueAssessmentData;
  setData: Dispatch<SetStateAction<ValueAssessmentData>>;
  updateSection: (section: keyof ValueAssessmentData, values: any, options?: UpdateSectionOptions) => ValueAssessmentData | undefined;
  saveSummarySection: (summaryPatch: Record<string, any>, options?: SaveSummarySectionOptions) => Promise<any>;
  isLoading: boolean;
  isSaving: boolean;
  valueSummary: ValueSummary | null;
  constructValueSummary: () => Promise<void>;
  fteAllocationMode: 'task' | 'pillar';
  setFteAllocationMode: (mode: 'task' | 'pillar') => void;
  validateModelingAllocation: (modelingBlob: any) => { allocationComplete: boolean; lastValidatedAt: string };
  saveImmediately: (data: ValueAssessmentData) => Promise<void>;
  updateValueSummaryScenarios: (scenarioData: {
    activeScenario: string;
    scenarios: Array<{
      type: "conservative" | "likely" | "stretch";
      automationRate: number;
      discountRate: number;
      projections: {
        roi: number;
        npv: number;
        paybackMonths: number;
        fiveYearValue: number;
      };
    }>;
    transformationLevers?: Record<string, number>;
  }) => Promise<void>;
  calculateAndPersistROI: () => Promise<{ roiData: any; investmentBreakdown: any } | null>;
  populateKeyImprovementLevers: (quadrantPlacement: string) => Promise<Array<{ title: string; category: string; priority?: string; pillar?: string; }>>;
  updateInputScreensKickoff: (data: { clientName: string; erpSystem: string; regionCoverage: string[]; toolsInUse: string; notes?: string; }) => Promise<void>;
  updateInputScreensStrategicContext: (data: { priorities: string[]; painPoints: string[]; transformationDrivers?: string[]; }) => Promise<void>;
  updateInputScreensResourceAllocation: (data: { regionFTEs: { [regionName: string]: number }; avgCostPerFTE: { [regionName: string]: number }; totalFTEs: number; }) => Promise<void>;
  updatePricingSummary: (pricingData: any) => Promise<void>;
  project?: ProjectPayload;
};

const ValueAssessmentContext = createContext<ContextType | null>(null);

export function useValueAssessment() {
  const ctx = useContext(ValueAssessmentContext);
  if (!ctx) throw new Error("useValueAssessment must be used within provider");
  return ctx;
}

export function ValueAssessmentProvider({ children }: { children: ReactNode }) {
  const params = useParams();
  const [location] = useLocation();
  
  // Get projectId from URL params (traditional approach)
  const projectId = (params as any).projectId;
  // Public token awareness for autosave routing
  const qs = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null;
  const publicToken = (qs?.get('t') || (typeof window !== 'undefined' ? sessionStorage.getItem('public_token') || '' : '')) as string;
  const isPublic = Boolean(publicToken);
  

  const [data, setData] = useState<ValueAssessmentData>({
    kickoff: {
      clientName: '',
      erpSystems: [],
      revenueBand: '',
      legalEntities: 0,
      strategicGoals: [],
      targetCloseDays: 0,
      currentCloseDays: 0,
    },
    calculationRulesVersion: "v2.0",
    valueSummaryVersion: "v2.1",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [valueSummary, setValueSummary] = useState<ValueSummary | null>(null);
  const [fteAllocationMode, setFteAllocationMode] = useState<'task' | 'pillar'>('task');
  // const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const initialLoadRef = useRef(false);
  const [project, setProject] = useState<ProjectPayload | undefined>();

  useEffect(() => {
    (async () => {
      try {
        const response = await apiRequest('/api/value-assessment/rationale-defaults');
        if (response && typeof response === 'object') {
          setRuntimeActivityHelpTextConfig({
            defaultText: response.defaultText,
            activityMap: response.activityMap,
          });
        }
      } catch (error) {
        console.warn('Failed to load runtime rationale defaults, using static defaults.', error);
      }
    })();
  }, []);


  /**
   * Value Assessment Data Initialization and Hydration System
   * 
   * This effect handles the complete data lifecycle for Value Assessment:
   * 1. Project validation and Value Assessment record initialization
   * 2. Data hydration from the database (summary_data.valueSummary v2.1)
   * 3. Context state population with legacy data migration support
   * 4. ValueSummary construction for export system integration
   * 
   * Supports both new assessments and resuming completed ones with full state restoration
   */
  useEffect(() => {
    async function initAndFetchData() {
      if (!projectId || initialLoadRef.current) return;
      // Public token present: skip private initialization and data load
      try {
        const params = new URLSearchParams(window.location.search)
        const publicToken = params.get('t') || sessionStorage.getItem('public_token')
        if (publicToken) {
          // Public mode: load limited data via public endpoint for hydration
          setIsLoading(true)
          try {
            const res = await fetch(`/api/public/value-assessment/${publicToken}`)
            if (res.ok) {
              const json = await res.json()
              const kickoff = (json && (json.kickoff || (json.data && json.data.summary_data && json.data.summary_data.kickoff))) || {}
              setData(prev => ({
                ...prev,
                kickoff: {
                  clientName: kickoff.clientName || '',
                  erpSystems: Array.isArray(kickoff.erpSystems) ? kickoff.erpSystems : [],
                  legalEntities: kickoff.legalEntities || 0,
                  revenueBand: kickoff.revenueBand || '',
                  currentCloseDays: kickoff.currentCloseDays || 0,
                  targetCloseDays: kickoff.targetCloseDays || 0,
                  strategicGoals: Array.isArray(kickoff.strategicGoals) ? kickoff.strategicGoals : []
                }
              }))
            }
          } catch (e) {
            console.error('[PUBLIC LOAD] Failed to hydrate kickoff via token endpoint', e)
          } finally {
            setIsLoading(false)
            initialLoadRef.current = true
          }
          return
        }
      } catch {}
      
      setIsLoading(true);
      try {
        // Initialize Value Assessment record in the database if not exists
        const token = localStorage.getItem('jwt_token');
        const initResponse = await fetch(`/api/value-assessment/init/${projectId}`, {
          method: "POST",
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {})
          }
        });
        
        if (initResponse.ok) {
          // const initData = await initResponse.json();
          // console.log(`✅ Value Assessment initialized for project ${projectId}`);
          
          // Then load the existing data with cache-busting
          const response = await fetch(`/api/value-assessment/${projectId}?t=${Date.now()}`, {
            headers: {
              'Cache-Control': 'no-cache',
              'Pragma': 'no-cache',
              ...(token ? { Authorization: `Bearer ${token}` } : {})
            }
          });
          if (response.ok) {
            const json = await response.json();
            if (json.project) {
              setProject(json.project);
            }

            if (json && json.data) {
              // NEW: Hydrate from summary_data structure
              const summaryData = json.data.summary_data || {};

              const taskFTEData = json.data?.summary_data?.taskFTEAllocation || {};
              const migratedStrategicContext = json.strategicContext || {};
              const migratedPillarSelection = json.configuration || {};
              const migratedValueModeling = json.modeling || {};
              
              // Extract authentic kickoff data from actual locations
              // CRITICAL FIX: Map API region names to frontend format
              const mapRegionData = (regionData: any) => {
                if (!regionData) return {
                  americas: 0,
                  emea: 0,
                  apac: 0,
                  india: 0,
                  shared_services: 0
                };
                
                return {
                  americas: regionData["North America"] || regionData.americas || 0,
                  emea: regionData["EMEA"] || regionData.emea || 0,
                  apac: regionData["APAC"] || regionData.apac || 0,
                  india: regionData["India"] || regionData.india || 0,
                  shared_services: regionData["Shared Services"] || regionData.shared_services || 0
                };
              };

              // Get kickoff data from the API response if available
              const apiKickoffData = json.kickoff || {};

              const dataWithDefaults = {
                kickoff: {
                  clientName: apiKickoffData.clientName,
                  erpSystems: apiKickoffData.erpSystems,
                  revenueBand: apiKickoffData.revenueBand,
                  legalEntities: apiKickoffData.legalEntities,
                  currentCloseDays: apiKickoffData.currentCloseDays,
                  targetCloseDays: apiKickoffData.targetCloseDays,
                  strategicGoals: apiKickoffData.strategicGoals,
                  // Use authentic FTE data from taskFTEAllocation
                  regionalFTEs: apiKickoffData.regionalFTEs,
                  fteCostsByRegion: apiKickoffData.fteCostsByRegion
                },
                context: {
                  painPoints: migratedStrategicContext.painPoints || [],
                  blockers: migratedStrategicContext.implementationBlockers || migratedStrategicContext.blockers || [],
                  tools: migratedStrategicContext.currentTools || migratedStrategicContext.tools || [],
                  currency: migratedStrategicContext.strategicReadiness || migratedStrategicContext.currency || 'USD',
                  otherPainPoints: migratedStrategicContext.otherPainPoints || '',
                  otherBlockers: migratedStrategicContext.otherBlockers || '',
                  revenue: migratedStrategicContext.revenue || apiKickoffData.revenueBand,
                  entities: migratedStrategicContext.entities || apiKickoffData.legalEntities,
                  focusAreas: migratedStrategicContext.focusAreas || apiKickoffData.strategicGoals,
                  toolsAndPlatforms: migratedStrategicContext.toolsAndPlatforms || []
                },
                configuration: {
                  selectedPillars: migratedPillarSelection.selectedPillars || [],
                  pillarPainPoints: migratedPillarSelection.pillarPainPoints || {}
                },
                // Add pillarSelection field to preserve backend data structure
                pillarSelection: migratedPillarSelection,
                valueSummary: summaryData.valueSummary || json.data.summary_data?.valueSummary || {},
                roi_data: summaryData.roi_data || json.data.roi_data, 
                summary_data: json.data.summary_data || {},
                // CRITICAL FIX: Add FTE allocation data mapping with automation percentages
                taskFTEAllocation: summaryData.taskFTEAllocation || taskFTEData || {},
                fteAllocation: summaryData.fteAllocation || json.data.fteAllocation || {},
                modeling: {
                  allocationMode: migratedValueModeling.selectedAllocationMode || migratedValueModeling.allocationMode || 'task',
                  // CRITICAL: Use migrated FTE data for value modeling (same as kickoff)
                  regionalFTEs: mapRegionData(migratedValueModeling.regionalFTEs) || apiKickoffData.regionalFTEs,
                  regionalFTECosts: mapRegionData(migratedValueModeling.regionalFTECosts) || apiKickoffData.fteCostsByRegion || {
                    americas: 0,
                    emea: 0,
                    apac: 0,
                    india: 0,
                    shared_services: 0
                  },
                  taskAllocations: migratedValueModeling.taskAllocations || {},
                  pillarAllocations: migratedValueModeling.pillarAllocations || {},
                  applyBenchmarks: migratedValueModeling.applyBenchmarks !== undefined ? migratedValueModeling.applyBenchmarks : true,
                  collapsedPillars: migratedValueModeling.collapsedPillars || {},
                  taskOverrides: migratedValueModeling.taskOverrides || {},
                  validation: migratedValueModeling.validation || {
                    allocationComplete: false,
                    lastValidatedAt: null
                  },
                  // CRITICAL FIX: Map automation percentages from API to automationLevels format
                  automationLevels: migratedValueModeling.automationLevels || taskFTEData.automationPercentages || {},
                  selectedAllocationMode: migratedValueModeling.selectedAllocationMode || migratedValueModeling.allocationMode || 'task'
                },
                // CRITICAL FIX: Add valueModeling field for CostSavingsSummary component compatibility
                valueModeling: {
                  taskAllocations: migratedValueModeling.taskAllocations || {},
                  // CRITICAL FIX: Map automation percentages from API to automationLevels format  
                  automationLevels: migratedValueModeling.automationLevels || taskFTEData.automationPercentages || {},
                  selectedAllocationMode: migratedValueModeling.selectedAllocationMode || migratedValueModeling.allocationMode || 'task'
                },
                // Add financialMetrics for component compatibility
                financialMetrics: summaryData.financialMetrics || {},
                // Ensure required version fields exist to satisfy ValueAssessmentData
                calculationRulesVersion: "v2.0",
                valueSummaryVersion: "v2.1",
              };
              setData(dataWithDefaults);
              
              // CRITICAL FIX: Extract and set valueSummary state for component access
              // if (dataWithDefaults.valueSummary && Object.keys(dataWithDefaults.valueSummary).length > 0) {
                // console.log('🔧 CRITICAL FIX: Setting valueSummary state from extracted data');
                // setValueSummary(dataWithDefaults.valueSummary);
              // }
            }
          }
        }
      } catch (error) {
        console.error('Failed to initialize/load value assessment data:', error);
      } finally {
        setIsLoading(false);
        initialLoadRef.current = true;
      }
    }
    
    initAndFetchData();
  }, [projectId]);

  /**
   * Auto-save System for Real-time Data Persistence
   * 
   * This effect implements debounced auto-save functionality that:
   * 1. Monitors all context data changes with 2-second debounce
   * 2. Structures data into summary_data.valueSummary v2.1 format
   * 3. Persists to database via PUT /api/value-assessment/{projectId}
   * 4. Maintains cross-screen state continuity and export readiness
   * 
   * Auto-save triggers on any data state change ensuring no user input is lost
   * Data is structured for optimal export generation and ROI calculation integration
   */

  const buildStructuredPayload = (updated: ValueAssessmentData) => {
    const hasPillar = !!(updated?.taskFTEAllocation?.pillarFTEAllocation) || (updated?.taskFTEAllocation?.allocationMode === 'pillar');
    const schemaVersion = hasPillar ? 'v2.2' : 'v2.1';

    return {
      summary_data: {
        schemaVersion,
        lastUpdatedAt: new Date().toISOString(),
        
        // ✅ KEEP: Primary FTE allocation data (now includes allocationMode/pillarFTEAllocation if present)
        taskFTEAllocation: updated.taskFTEAllocation || updated.summary_data?.taskFTEAllocation || {},
        
        // ✅ KEEP: Core sections  
        kickoff: updated.kickoff || {},
        pillarSelection: updated.pillarSelection || updated.configuration || {},
        configuration: updated.configuration || {},
        
        // ✅ KEEP: ROI data with database-driven calculations
        roi_data: updated.roi_data || {},
        
        // 🧮 CENTRALIZED: Financial calculations via formula engine
        valueSummary: {
          schemaVersion,
          lastUpdatedAt: new Date().toISOString(),
          
          // Core client/project data
          kickoff: updated.kickoff || {},
          taskFTEAllocation: updated.taskFTEAllocation || updated.summary_data?.taskFTEAllocation || {},
          pillarSelection: updated.pillarSelection || updated.configuration || {},
          configuration: updated.configuration || {},
          roi_data: updated.roi_data || {}
        }
      },
      roi_data: updated.roi_data || {}
    };
  };

  const updateSection = (section: keyof ValueAssessmentData, values: any, options: UpdateSectionOptions = {}) => {
    const shouldAutoSave = options.autoSave !== false;
    let updatedState: ValueAssessmentData | undefined;
    // console.log(`🔄 Updating section: ${section}`, values);
    
    setData((prev) => {
      const prevSection: any = (prev as any)[section];
      const isObject = (val: any) => val && typeof val === 'object' && !Array.isArray(val);

      const resolvedSection = typeof values === 'function'
        ? (values as (prevValue: any) => any)(prevSection)
        : values;

      const mergedSection = isObject(prevSection) && isObject(resolvedSection)
        ? { ...prevSection, ...resolvedSection }
        : resolvedSection;

      const updated = {
        ...prev,
        [section]: mergedSection as any,
      } as ValueAssessmentData;
      
      updatedState = updated;

      // Use the debouncedAutoSave hook to handle saving with proper debouncing when enabled
      if (projectId && shouldAutoSave) {
        const structuredData = buildStructuredPayload(updated);

        if (isPublic) {
          saveData({ summary_data: structuredData.summary_data } as any);
        } else {
          saveData({ data: structuredData } as any);
        }
      }
      
      return updated;
    });
    return updatedState;
  };

  const persistSummarySection = async (summaryPatch: Record<string, any>, options: SaveSummarySectionOptions = {}) => {
    if (!projectId || !summaryPatch || Object.keys(summaryPatch).length === 0) return null;

    const endpoint = isPublic ? `/api/public/value-assessment/${publicToken}` : `/api/value-assessment/${projectId}`;
    const payload = isPublic
      ? { summary_data: summaryPatch }
      : { data: { summary_data: summaryPatch } };

    const keepAlive = options.keepAlive === true;

    if (!keepAlive) {
      setIsSaving(true);
    }
    try {
      if (keepAlive) {
        const headers: Record<string, string> = {
          'Content-Type': 'application/json'
        };

        if (!isPublic) {
          const token = typeof window !== 'undefined' ? localStorage.getItem('jwt_token') : null;
          if (token) {
            headers['Authorization'] = `Bearer ${token}`;
          }
        }

        await fetch(endpoint, {
          method: 'PUT',
          headers,
          body: JSON.stringify(payload),
          keepalive: true,
        });
        return null;
      }

      if (isPublic) {
        const res = await fetch(endpoint, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        if (!res.ok) {
          const errorText = await res.text();
          throw new Error(errorText || 'Failed to save value assessment (public)');
        }
        return await res.json();
      }

      return await apiRequest(endpoint, {
        method: 'PUT',
        body: payload
      });
    } catch (error) {
      console.error('[ValueAssessment] Failed to persist summary section:', error);
      throw error;
    } finally {
      if (!keepAlive) {
        setIsSaving(false);
      }
    }
  };
  
  // Setup the debounced auto-save hook
  const { debouncedSave: saveData, isSaving: isSavingData } = useDebouncedAutoSave({
    debounceMs: 2000, // 2 second debounce for better performance
    endpoint: isPublic ? `/api/public/value-assessment/${publicToken}` : `/api/value-assessment/${projectId}`,
    method: isPublic ? 'PUT' : 'POST',
    onSuccess: () => {
      // console.log(`✅ [DEBOUNCED SAVE] Data saved successfully`);
    },
    onError: (error) => {
      console.error(`❌ [DEBOUNCED SAVE] Failed to save data:`, error);
    }
  });
  
  // Update the isSaving state based on the hook's state
  useEffect(() => {
    setIsSaving(isSavingData);
  }, [isSavingData]);



  /**
   * Value Modeling Allocation Validation System
   * 
   * Validates the completeness and accuracy of FTE allocation across tasks/pillars:
   * - Checks allocation mode selection (task vs pillar)
   * - Validates allocation percentage completion
   * - Ensures data consistency between selected mode and available data
   * - Returns validation status with detailed error reporting
   * 
   * Used by Value Modeling screen to enforce business rules and guide user completion
   */
  const validateModelingAllocation = (modelingBlob: any) => {
    if (!modelingBlob) {
      return { 
        allocationComplete: false, 
        lastValidatedAt: new Date().toISOString()
      };
    }

    const errors = [];
    let allocationComplete = false;

    // Validate allocation mode selection
    if (!modelingBlob.allocationMode) {
      errors.push('Allocation mode not selected');
    }

    // Validate allocation completeness from validation data
    if (modelingBlob.validation) {
      allocationComplete = modelingBlob.validation.allocationComplete;
      if (!allocationComplete) {
        errors.push(`Allocation incomplete: ${modelingBlob.validation.allocationPercentage?.toFixed(0) || 0}%`);
      }
    }

    // Validate data consistency for the selected allocation mode
    if (modelingBlob.allocationMode === 'task' && !modelingBlob.taskAllocations) {
      errors.push('Task allocation data missing for selected mode');
    }
    
    if (modelingBlob.allocationMode === 'pillar' && !modelingBlob.pillarAllocations) {
      errors.push('Pillar allocation data missing for selected mode');
    }

    return { 
      allocationComplete,
      lastValidatedAt: new Date().toISOString(),
      errors: errors.length > 0 ? errors : null,
      selectedMode: modelingBlob.allocationMode,
      validationData: modelingBlob.validation
    };
  };

  /**
   * ValueSummary Construction Engine for Export System Integration
   * 
   * This function constructs the complete valueSummary v2.1 structure for PowerPoint/Excel exports:
   * 1. Aggregates data from all Value Assessment screens (kickoff, context, modeling, etc.)
   * 2. Integrates financial calculations from database-driven ROI engine
   * 3. Incorporates maturity assessment data and quadrant analysis
   * 4. Prepares export-ready data structures with proper formatting
   * 5. Maintains backward compatibility with legacy data structures
   * 
   * The resulting valueSummary serves as single source of truth for all export operations
   */
  const constructValueSummary = async () => {
    if (!projectId) return;

    try {
      // console.log("🔧 constructValueSummary: Starting with data:", {
      //   hasROIData: !!data.roi_data,
      //   hasSummaryData: !!data.summary_data,
      //   roiDataKeys: data.roi_data ? Object.keys(data.roi_data) : [],
      //   summaryDataKeys: data.summary_data ? Object.keys(data.summary_data) : []
      // });

      // Extract ROI data and existing valueSummary from v2.1 structure
      const roiData = data.summary_data?.roi_data || data.roi_data || {};
      const existingValueSummary = data.summary_data?.valueSummary || data.valueSummary || {};

      
      // Use existing complete valueSummary if available (optimization for completed assessments)
      if (existingValueSummary && Object.keys(existingValueSummary).length > 0) {
        setValueSummary(existingValueSummary);
        return;
      }



      // Construct the comprehensive valueSummary using existing ROI data
      const clientName = data.kickoff?.clientName || '';
      const entityCount = data.kickoff?.legalEntities || 10;


      const summary: ValueSummary = {
        // Core metadata
        kickoff: data.kickoff,
        clientName: clientName,
        projectName: 'Value Assessment Project',
        assessmentDate: new Date().toLocaleDateString(),
        entityCount: entityCount,
        
        // Project structure for backward compatibility
        project: {
          id: projectId,
          name: 'Value Assessment Project',
          clientName: clientName,
          createdAt: new Date().toISOString(),
        },
        
        // Selected configuration
        selectedPillars: data.configuration?.selectedPillars || [],
        taskSelections: [], // Will be populated from modeling data if available
        fteInputs: [], // Will be populated from kickoff data if available
        
        // Pricing structure from ROI data
        pricing: roiData.totalInvestment ? {
          revenueBand: data.kickoff?.revenueBand || 'enterprise',
          platformCost: roiData.totalInvestment || 0,
          entityMultiplier: 1,
          totalInvestment: roiData.totalInvestment || 0,
          pricingTier: 'Standard',
          yearlyBreakdown: []
        } : {
          revenueBand: 'unknown',
          platformCost: 0,
          entityMultiplier: 1,
          totalInvestment: 0,
          pricingTier: 'Standard'
        },
        
        // Financial metrics from existing ROI data
        financialMetrics: {
          npv: roiData.npv || 0,
          roi: roiData.roi || 0,
          paybackPeriod: roiData.paybackPeriod || 0,
          totalCostOfOwnership: roiData.totalInvestment || 0,
          fiveYearValue: roiData.five_year_value || roiData.fiveYearValue || 0,
          investmentEfficiency: roiData.totalInvestment > 0 ? (roiData.annualSavings || 0) / roiData.totalInvestment : 0,
          costOfInaction: {
            annualLoss: (roiData.annualSavings || 0) * 0.1, // 10% of potential savings
            monthlyLoss: ((roiData.annualSavings || 0) * 0.1) / 12,
            calculatedMethod: 'percentage_of_savings'
          }
        },
        
        // Strategic context data (SCHEMA EXPANSION)
        strategicContext: {
          painPoints: data.context?.painPoints || [],
          implementationBlockers: data.context?.blockers || [],
          currentTools: data.context?.tools?.map(t => ({ 
            name: typeof t === 'string' ? t : t.name, 
            cost: typeof t === 'object' ? (t.licenseCost || t.serviceCost) : undefined 
          })) || [],
          strategicReadiness: "Not assessed"
        },

        // Simplified scenario analysis (SCHEMA EXPANSION)
        scenarios: [
          {
            type: "conservative" as const,
            automationRate: 0.6,
            discountRate: 0.12,
            projections: {
              roi: (roiData.roi || 0) * 0.8,
              npv: (roiData.npv || 0) * 0.8,
              paybackMonths: (roiData.paybackMonths || 0) * 1.2,
              fiveYearValue: (roiData.fiveYearValue || 0) * 0.8
            }
          },
          {
            type: "likely" as const,
            automationRate: 0.8,
            discountRate: 0.08,
            projections: {
              roi: roiData.roi || 0,
              npv: roiData.npv || 0,
              paybackMonths: roiData.paybackMonths || 0,
              fiveYearValue: roiData.fiveYearValue || 0
            }
          },
          {
            type: "stretch" as const,
            automationRate: 0.9,
            discountRate: 0.06,
            projections: {
              roi: (roiData.roi || 0) * 1.2,
              npv: (roiData.npv || 0) * 1.2,
              paybackMonths: (roiData.paybackMonths || 0) * 0.8,
              fiveYearValue: (roiData.fiveYearValue || 0) * 1.2
            }
          }
        ],

        // Export-ready data for PowerPoint and other formats (SCHEMA EXPANSION)
        exportData: {
          powerpointData: {
            irrFormatted: roiData.irr ? `${(roiData.irr * 100).toFixed(1)}%` : "",
            paybackFormatted: roiData.paybackMonths ? `${Math.round(roiData.paybackMonths)} months` : "",
            quadrantLabel: "", //maturity?.quadrant || "",
            topUseCases: data.configuration?.selectedPillars?.slice(0, 3) || [],
            scenarioSummary: "Conservative, Likely, and Stretch scenarios analyzed",
            selectedPillarsSummary: data.configuration?.selectedPillars?.join(", ") || "",
            financialSnapshot: {
              totalInvestment: roiData.totalInvestment || 0,
              annualSavings: roiData.annualSavings || 0,
              fiveYearValue: roiData.fiveYearValue || 0,
              roi: roiData.roi || 0
            }
          }
        },

        // Legacy scenario analysis structure (backward compatibility)
        scenarioAnalysis: {
          baseline: {
            automationRate: 0.8,
            discountRate: 0.08,
            leverOverrides: {},
            timestamp: new Date().toISOString()
          },
          conservative: {
            automationRate: 0.6,
            discountRate: 0.12,
            leverOverrides: {},
            timestamp: new Date().toISOString()
          },
          stretch: {
            automationRate: 0.9,
            discountRate: 0.06,
            leverOverrides: {},
            timestamp: new Date().toISOString()
          },
          activeScenario: "baseline" as const,
          appliedScenario: {
            label: "Baseline",
            automationRate: 0.8,
            discountRate: 0.08,
            leverOverrides: {}
          },
          allLevers: []
        },
        
        // Version control
        calculationRulesVersion: "v2.0",
        valueSummaryVersion: "v2.1"
      };

      setValueSummary(summary);

      // Persist to database
      try {
        await fetch(`/api/projects/${projectId}/value-summary`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(summary),
        });
        console.log("✅ constructValueSummary: Saved to database");
      } catch (saveError) {
        console.error('Failed to save value summary to database:', saveError);
      }

    } catch (error) {
      console.error('Failed to construct value summary:', error);
      // Set a minimal summary to prevent the error
      setValueSummary({
          kickoff: data.kickoff,
        clientName: data.kickoff?.clientName || 'Client',
        projectName: 'Value Assessment',
        assessmentDate: new Date().toLocaleDateString(),
        entityCount: data.kickoff?.legalEntities || 10,
        selectedPillars: [],
        taskSelections: [],
        fteInputs: [],
        roiData: data.roi_data,
        pricing: {
          revenueBand: 'unknown',
          platformCost: 0,
          entityMultiplier: 1,
          totalInvestment: 0,
          pricingTier: 'Standard'
        },
        financialMetrics: {
          npv: 0,
          roi: 0,
          paybackPeriod: 0,
          totalCostOfOwnership: 0,
          fiveYearValue: 0,
          investmentEfficiency: 0,
          costOfInaction: {
            annualLoss: 0,
            monthlyLoss: 0,
            calculatedMethod: 'fallback'
          }
        },
        scenarioAnalysis: {
          baseline: { automationRate: 0.8, discountRate: 0.08, leverOverrides: {}, timestamp: new Date().toISOString() },
          conservative: { automationRate: 0.6, discountRate: 0.12, leverOverrides: {}, timestamp: new Date().toISOString() },
          stretch: { automationRate: 0.9, discountRate: 0.06, leverOverrides: {}, timestamp: new Date().toISOString() },
          activeScenario: "baseline" as const,
          appliedScenario: { label: "Baseline", automationRate: 0.8, discountRate: 0.08, leverOverrides: {} },
          allLevers: []
        },
        calculationRulesVersion: "v2.0",
        valueSummaryVersion: "v2.0"
      });
    }
  };

  // Enhanced save function for immediate persistence
  const saveImmediately = async (dataToSave: ValueAssessmentData) => {
    if (!projectId) return;
    
    try {
      await fetch(`/api/value-assessment/${projectId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ data: dataToSave }),
      });
      // console.log('[ValueAssessmentContext] ✅ Immediate save completed');
    } catch (error) {
      console.error('[ValueAssessmentContext] Failed immediate save:', error);
      throw error;
    }
  };

  /**
   * Input Screens Data Management Functions
   * 
   * These functions manage the inputScreens section of the valueSummary blob
   * for Project Kickoff, Strategic Context, and Resource Allocation screens
   */
  
  // Update Project Kickoff data in inputScreens
  const updateInputScreensKickoff = async (kickoffData: {
    clientName: string;
    erpSystem: string;
    regionCoverage: string[];
    toolsInUse: string;
    notes?: string;
  }) => {
    if (!projectId) return;
    
    try {

      const payload = {
        summary_data: {
          valueSummary: {
            inputScreens: {
              projectKickoff: kickoffData
            }
          }
        }
      };

      const response = await fetch(`/api/value-assessment/${projectId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ data: payload }),
      });
      
      if (!response.ok) {
        throw new Error(`Failed to save kickoff data: ${response.status}`);
      }
      

      // Update local valueSummary state
      setValueSummary(prev => prev ? {
        ...prev,
        inputScreens: {
          ...prev.inputScreens,
          projectKickoff: kickoffData
        }
      } : null);
      
    } catch (error) {
      console.error('Failed to update kickoff inputScreens:', error);
      throw error;
    }
  };

  // Update Strategic Context data in inputScreens
  const updateInputScreensStrategicContext = async (contextData: {
    priorities: string[];
    painPoints: string[];
    transformationDrivers?: string[];
  }) => {
    if (!projectId) return;
    
    try {

      const payload = {
        summary_data: {
          valueSummary: {
            inputScreens: {
              strategicContext: contextData
            }
          }
        }
      };

      const response = await fetch(`/api/value-assessment/${projectId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ data: payload }),
      });
      
      if (!response.ok) {
        throw new Error(`Failed to save strategic context data: ${response.status}`);
      }
      

      // Update local valueSummary state
      setValueSummary(prev => prev ? {
        ...prev,
        inputScreens: {
          ...prev.inputScreens,
          strategicContext: contextData
        }
      } : null);
      
    } catch (error) {
      console.error('Failed to update strategic context inputScreens:', error);
      throw error;
    }
  };

  // Update Resource Allocation data in inputScreens
  const updateInputScreensResourceAllocation = async (resourceData: {
    regionFTEs: { [regionName: string]: number };
    avgCostPerFTE: { [regionName: string]: number };
    totalFTEs: number;
  }) => {
    if (!projectId) return;
    
    try {

      const payload = {
        summary_data: {
          valueSummary: {
            inputScreens: {
              resourceAllocation: resourceData
            }
          }
        }
      };

      const response = await fetch(`/api/value-assessment/${projectId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ data: payload }),
      });
      
      if (!response.ok) {
        throw new Error(`Failed to save resource allocation data: ${response.status}`);
      }
      

      // Update local valueSummary state
      setValueSummary(prev => prev ? {
        ...prev,
        inputScreens: {
          ...prev.inputScreens,
          resourceAllocation: resourceData
        }
      } : null);
      
    } catch (error) {
      console.error('Failed to update resource allocation inputScreens:', error);
      throw error;
    }
  };

  // Update valueSummary scenarios for PowerPoint export enhancement
  const updateValueSummaryScenarios = async (scenarioData: {
    activeScenario: string;
    scenarios: Array<{
      type: "conservative" | "likely" | "stretch";
      automationRate: number;
      discountRate: number;
      projections: {
        roi: number;
        npv: number;
        paybackMonths: number;
        fiveYearValue: number;
      };
    }>;
    transformationLevers?: Record<string, number>;
  }) => {
    if (!projectId) return;
    
    try {

      const scenariosPayload = {
        summary_data: {
          scenarios: {
            activeScenario: scenarioData.activeScenario,
            scenarios: scenarioData.scenarios,
            transformationLevers: scenarioData.transformationLevers || {},
            lastUpdatedAt: new Date().toISOString(),
            schemaVersion: 'v2.1'
          }
        }
      };

      await fetch(`/api/value-assessment/${projectId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ data: scenariosPayload }),
      });
      

      // Refresh valueSummary to reflect changes
      // await constructValueSummary();
      
    } catch (error) {
      console.error('[ValueAssessmentContext] Failed to update scenarios:', error);
      throw error;
    }
  };

  // PROMPT 5: ROI & Investment Calculations and Persistence
  const calculateAndPersistROI = async (): Promise<{ roiData: any; investmentBreakdown: any; } | null> => {

    if (!projectId || !data) {
      console.log('❌ Missing projectId or data for ROI calculations');
      return null;
    }

    try {
      const regionalFTEs = {};
      // Extract FTE costs from taskFTEAllocation data where it's actually stored
      const fteCostsByRegion = data.taskFTEAllocation?.fteCostsByRegion || 
                               data.summary_data?.taskFTEAllocation?.fteCostsByRegion ||
                               null;
      
      if (!fteCostsByRegion) {
        console.error('[ROI CALC] CRITICAL: No FTE costs found in assessment data');
        console.error('[ROI CALC] All FTE costs must come from database - no hardcoded fallbacks allowed');
        return null;
      }


      // STEP 2: PERFORM CALCULATIONS
      // Calculate annual savings from automation
      let annualSavings = 0;
      const totalFTEs = Object.values(regionalFTEs).reduce((sum: number, fte: any) => sum + (fte || 0), 0);
      
      // CRITICAL FIX: Use activity-specific automation rates from automationLevels
      const finalAutomationLevels = data.modeling?.automationLevels || data.valueModeling?.automationLevels || {};
      
      if (!finalAutomationLevels || Object.keys(finalAutomationLevels).length === 0) {
        console.error('[ROI CALC] CRITICAL: No automation levels found in assessment data');
        console.error('[ROI CALC] Each activity must have its automation rate set - no fallbacks allowed');
        return null;
      }
      
      // Calculate savings using regional FTE allocation and activity-specific automation rates
      Object.entries(regionalFTEs).forEach(([regionName, regionFTEs]: [string, any]) => {
        // Handle different region name formats in data
        const regionFTECost = fteCostsByRegion[regionName] || 
                              fteCostsByRegion[regionName.toLowerCase()] ||
                              fteCostsByRegion[regionName.replace(/\s+/g, '_').toLowerCase()] ||
                              fteCostsByRegion['North America']; // Common case

        if (!regionFTECost) {
          console.error(`❌ No FTE cost found for region: ${regionName}`);
          console.error('[ROI CALC] Available FTE cost regions:', Object.keys(fteCostsByRegion));
          return;
        }
        
        // Use FTE allocation data if available, otherwise distribute evenly
        const fteAllocation = data.fteAllocation?.fteAllocation?.regions?.find((r: any) => r.regionName === regionName);
        
        if (fteAllocation?.activityBreakdown) {
          // CORRECT FORMULA: FTE per region × automation rate per activity × FTE cost per region
          Object.entries(fteAllocation.activityBreakdown).forEach(([activity, ftes]: [string, any]) => {
            const automationRate = finalAutomationLevels[activity] || 0;
            const ftesNumber = typeof ftes === 'number' ? ftes : parseFloat(ftes) || 0;
            const automationDecimal = automationRate / 100;
            const ftesSaved = ftesNumber * automationDecimal;
            const activitySavings = ftesSaved * regionFTECost;
            
            annualSavings += activitySavings;
            
            // console.log(`💰 ${regionName} - ${activity}: ${ftesNumber} FTE × ${automationRate}% × $${regionFTECost.toLocaleString()} = $${Math.round(activitySavings).toLocaleString()}`);
          });
        } else {
          // Fallback: distribute region FTEs across automation activities using same formula
          const activitiesWithRates = Object.keys(finalAutomationLevels);
          const ftesPerActivity = regionFTEs / activitiesWithRates.length;
          
          activitiesWithRates.forEach(activity => {
            const automationRate = finalAutomationLevels[activity];
            const automationDecimal = automationRate / 100;
            const ftesSaved = ftesPerActivity * automationDecimal;
            const activitySavings = ftesSaved * regionFTECost;
            
            annualSavings += activitySavings;
            
            // console.log(`💰 ${regionName} - ${activity} (distributed): ${ftesPerActivity} FTE × ${automationRate}% × $${regionFTECost.toLocaleString()} = $${Math.round(activitySavings).toLocaleString()}`);
          });
        }
      });

      // Calculate investment costs - MUST COME FROM DATABASE PRICING
      console.error('[ROI CALC] CRITICAL: Investment calculations removed - must use database pricing system');
      console.error('[ROI CALC] Use calculateInvestmentBreakdown() from usePricingData hook instead');
      return null;
    } catch (error) {
      console.log('❌ Error calculating and persisting ROI:', error);
      return null;
    }
  };

  // Generate Key Improvement Levers based on Maturity Quadrant
  const generateKeyImprovementLevers = (quadrant: string): string => {
    console.log('🎯 Generating Key Improvement Levers for quadrant:', quadrant);
    
    // Map quadrant to improvement levers based on requirements
    const leverMappings: Record<string, string> = {
      // Foundation Builder (Stage 1: Manual)
      'Stage 1: Manual': "Standardize high-volume manual tasks; prioritize early automation wins like reconciliations and journals.",
      'Foundation Builder': "Standardize high-volume manual tasks; prioritize early automation wins like reconciliations and journals.",
      
      // Tactical Automator (Stage 2: Siloed)
      'Stage 2: Siloed': "Evolve from isolated task automation to orchestrated close processes; embed exception handling.",
      'Tactical Automator': "Evolve from isolated task automation to orchestrated close processes; embed exception handling.",
      
      // Process Transformer (Stage 3: Managed)
      'Stage 3: Managed': "Operationalize your strategy through governance, metrics, and CoE leadership to overcome rollout gaps.",
      'Stalled Transformer': "Operationalize your strategy through governance, metrics, and CoE leadership to overcome rollout gaps.",
      'Process Transformer': "Operationalize your strategy through governance, metrics, and CoE leadership to overcome rollout gaps.",
      
      // Visionary Orchestrator (Stage 4-5: Controlled to Autonomous)
      'Stage 4-5: Controlled to Autonomous': "Benchmark top-decile performance, pilot continuous close, and expand orchestration to adjacent finance areas.",
      'Visionary Orchestrator': "Benchmark top-decile performance, pilot continuous close, and expand orchestration to adjacent finance areas."
    };
    
    const levers = leverMappings[quadrant] || "Focus on building foundational automation capabilities while aligning with strategic business objectives.";
    
    // console.log('✨ Generated levers for quadrant:', quadrant, '->', levers);
    return levers;
  };

  // Populate Key Improvement Levers for Maturity Assessment (Legacy function for backward compatibility)
  const populateKeyImprovementLevers = async (quadrantPlacement: string): Promise<Array<{ title: string; category: string; priority?: string; pillar?: string; }>> => {
    try {
      console.log('🎯 Populating Key Improvement Levers for quadrant:', quadrantPlacement);
      
      // Generate the primary lever text based on quadrant
      const primaryLever = generateKeyImprovementLevers(quadrantPlacement);
      
      // Create a structured response for backward compatibility
      const keyImprovementLevers = [{
        title: primaryLever,
        category: 'Strategic Priority',
        priority: 'High',
        pillar: 'Cross-functional'
      }];
      
      console.log('✨ Generated Key Improvement Levers:', keyImprovementLevers.length);
      
      return keyImprovementLevers;
    } catch (error) {
      console.error('❌ Error populating key improvement levers:', error);
      return [];
    }
  };

  // Update pricing summary data in valueSummary blob
  const updatePricingSummary = async (pricingData: any) => {
    if (!projectId) return;
    
    try {
      console.log('💰 Updating pricing summary data:', pricingData);
      
      // Extract investment cost from pricing data
      const totalInvestment = pricingData.pricingSummary?.finalPrice || 0;
      console.log('💰 Including investment cost in ROI data:', totalInvestment);
      
      // Update both pricing summary and ROI data with investment costs
      const payload = {
        summary_data: {
          valueSummary: {
            pricingSummary: pricingData
          }
        },
        roi_data: {
          ...data.roi_data,
          totalInvestment: totalInvestment,
          dynamicMetrics: {
            ...data.roi_data?.dynamicMetrics,
            tco: totalInvestment
          }
        }
      };

      const response = await fetch(`/api/value-assessment/${projectId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ data: payload }),
      });
      
      if (!response.ok) {
        throw new Error(`Failed to save pricing summary data: ${response.status}`);
      }
      
      console.log('✅ Updated pricing summary and ROI data with investment costs');
      
      // Update local valueSummary state
      setValueSummary(prev => prev ? {
        ...prev,
        pricingSummary: pricingData
      } : null);
      
      // Update local data context
      setData(prev => {
        const updatedRoi = prev.roi_data
          ? {
              ...prev.roi_data,
              // Preserve existing required ROI fields
              five_year_value: prev.roi_data.five_year_value ?? 0,
              cumulative_benefit: prev.roi_data.cumulative_benefit ?? 0,
              investment_efficiency: prev.roi_data.investment_efficiency ?? 0,
              calculation_audit: prev.roi_data.calculation_audit ?? {
                total_cost_used: 0,
                calculation_method: 'manual',
                backend_calculated_cumulative: 0,
                backend_calculated_five_year_value: 0,
                backend_calculated_investment_efficiency: 0,
              },
              // Add/override dynamic metrics
              dynamicMetrics: {
                ...(prev.roi_data.dynamicMetrics || {} as any),
                tco: totalInvestment,
              } as any,
            }
          : {
              five_year_value: 0,
              cumulative_benefit: 0,
              investment_efficiency: 0,
              calculation_audit: {
                total_cost_used: 0,
                calculation_method: 'manual',
                backend_calculated_cumulative: 0,
                backend_calculated_five_year_value: 0,
                backend_calculated_investment_efficiency: 0,
              },
              dynamicMetrics: { tco: totalInvestment } as any,
            };

        return {
          ...prev,
          summary_data: {
            ...prev.summary_data,
            valueSummary: {
              ...prev.summary_data?.valueSummary,
              pricingSummary: pricingData,
            },
          },
          roi_data: updatedRoi,
        };
      });
      
    } catch (error) {
      console.error('Failed to update pricing summary:', error);
      throw error;
    }
  };

  return (
    <ValueAssessmentContext.Provider value={{ 
      data, 
      setData, 
      updateSection, 
      saveSummarySection: persistSummarySection,
      isLoading, 
      isSaving,
      valueSummary,
      constructValueSummary,
      fteAllocationMode,
      setFteAllocationMode,
      validateModelingAllocation,
      saveImmediately,
      updateValueSummaryScenarios,
      calculateAndPersistROI,
      populateKeyImprovementLevers,
      updateInputScreensKickoff,
      updateInputScreensStrategicContext,
      updateInputScreensResourceAllocation,
      updatePricingSummary,
      project
    }}>
      {children}
    </ValueAssessmentContext.Provider>
  );
}
